import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import clsx from 'clsx'
import type { ReactNode } from 'react'

type PanelSize = 'sm' | 'md' | 'lg'

interface SlidePanelProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  subtitle?: string
  size?: PanelSize
  actions?: ReactNode
}

const sizeMap: Record<PanelSize, string> = {
  sm: 'w-full sm:w-[380px]',
  md: 'w-full sm:w-[440px]',
  lg: 'w-full sm:w-[520px]',
}

export default function SlidePanel({ open, onClose, children, title, subtitle, size = 'md', actions }: SlidePanelProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(16,24,40,0.4)' }} className="animate-fade-in" onClick={onClose} />
      <div
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 99999 }}
        className={clsx('bg-white shadow-2xl flex flex-col slide-panel-enter', sizeMap[size])}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
            {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <button onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </>,
    document.body
  )
}
